import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Copy, Check, Share2, Gift, Clock, DollarSign,
  Users, ArrowRight, Loader2, Sparkles, ChevronRight,
  CheckCircle2, Trophy, Coins, Timer,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

function StatCard({ icon: Icon, label, value, color = "text-[#D3FF24]" }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6 flex flex-col gap-3">
      <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.75} />
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Share your code",
    desc: "Copy your unique referral link and share it with friends, colleagues, or anyone preparing for interviews.",
    icon: Share2,
  },
  {
    step: "02",
    title: "They sign up",
    desc: "When they create a free account using your link, they instantly get 20 bonus minutes added to their account.",
    icon: Users,
  },
  {
    step: "03",
    title: "You earn a reward",
    desc: "Once they join, you can claim 20 extra interview minutes OR request a ₹200 cash payout — your choice.",
    icon: Gift,
  },
];

export default function Referral() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [claiming, setClaiming] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/referral/stats");
      setStats(data);
    } catch {
      toast.error("Could not load referral data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyCode = () => {
    navigator.clipboard.writeText(stats.referral_code);
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(stats.referral_link);
    setCopiedLink(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Project Wingman — AI Interview Coach",
          text: `I use Wingman AI to prep for interviews. Join with my referral link and get 20 free bonus minutes! 🚀`,
          url: stats.referral_link,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  const claim = async (referralId, rewardType) => {
    setClaiming(p => ({ ...p, [referralId]: rewardType }));
    try {
      const { data } = await api.post("/referral/claim", {
        referral_id: referralId,
        reward_type: rewardType,
      });
      toast.success(data.message);
      await load();
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim reward");
    } finally {
      setClaiming(p => ({ ...p, [referralId]: null }));
    }
  };

  const freeRemaining = stats
    ? Math.max(0, stats.total_free_minutes - stats.free_minutes_used).toFixed(1)
    : null;

  return (
    <div className="px-10 py-10 max-w-5xl">
      {/* Hero */}
      <div className="mb-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3 flex items-center gap-2">
          <Coins className="w-3.5 h-3.5 text-[#D3FF24]" /> Earn with Wingman
        </div>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] mb-4">
          Refer friends.<br />
          <span className="text-[#D3FF24]" style={{ textShadow: "0 0 40px rgba(211,255,36,0.3)" }}>
            Earn minutes or cash.
          </span>
        </h1>
        <p className="text-neutral-400 text-lg max-w-xl leading-relaxed">
          Every friend you bring to Wingman earns you <strong className="text-white">20 bonus interview minutes</strong> or <strong className="text-white">₹200 cash</strong> — your choice, every time.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-neutral-500 py-20">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading your referral data…
        </div>
      ) : (
        <>
          {/* Referral Code Box */}
          <div className="border border-white/10 bg-[#0A0A0A] p-8 mb-10 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-[#D3FF24]" /> Your Referral Code
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                <div className="font-display text-4xl sm:text-5xl font-black tracking-[0.15em] text-white">
                  {stats.referral_code}
                </div>
                <button
                  onClick={copyCode}
                  className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:bg-white/5 transition"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-[#D3FF24]" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied!" : "Copy code"}
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 flex items-center bg-black border border-white/10 px-3 py-2.5 min-w-0">
                  <span className="text-xs text-neutral-500 truncate flex-1">{stats.referral_link}</span>
                </div>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-white/5 transition whitespace-nowrap"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-[#D3FF24]" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy link
                </button>
                <button
                  onClick={shareLink}
                  className="flex items-center gap-2 bg-[#D3FF24] text-black px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:brightness-105 transition whitespace-nowrap"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>
              </div>

              <div className="mt-4 flex items-center gap-2 text-[11px] text-neutral-500">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#D3FF24] shrink-0" />
                Referee gets 20 free bonus minutes instantly on sign-up
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
            <StatCard icon={Users}    label="Total Referrals"   value={stats.total_referrals} />
            <StatCard icon={Gift}     label="Rewards Pending"   value={stats.pending_rewards} color="text-amber-400" />
            <StatCard icon={Timer}    label="Minutes Earned"    value={`${stats.earned_minutes} min`} />
            <StatCard icon={DollarSign} label="Cash Earned"     value={`₹${stats.earned_payout}`} color="text-emerald-400" />
          </div>

          {/* Your free minutes status */}
          <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Your Free Interview Time</div>
                <div className="text-sm text-neutral-300">
                  <span className="text-white font-semibold">{freeRemaining} min</span> remaining of{" "}
                  <span className="text-white font-semibold">{stats.total_free_minutes} min</span> total
                  {stats.bonus_minutes > 0 && (
                    <span className="ml-2 text-[10px] text-[#D3FF24] border border-[#D3FF24]/30 px-2 py-0.5">
                      +{stats.bonus_minutes} min from referrals
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-[160px] max-w-xs">
                <div className="h-2 bg-white/5 w-full">
                  <div
                    className="h-full transition-all duration-700"
                    style={{
                      width: `${Math.min(100, (stats.free_minutes_used / stats.total_free_minutes) * 100)}%`,
                      background: freeRemaining > 5 ? "#D3FF24" : "#fb923c",
                    }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-neutral-600 mt-1">
                  <span>{stats.free_minutes_used.toFixed(1)} used</span>
                  <span>{stats.total_free_minutes} total</span>
                </div>
              </div>
              {user?.plan === "free" && (
                <button
                  onClick={() => nav("/pricing")}
                  className="flex items-center gap-2 bg-premium text-white px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:brightness-110 transition"
                >
                  Upgrade for unlimited <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Pending rewards */}
          {stats.pending_referrals.length > 0 && (
            <div className="mb-10">
              <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Pending Rewards</h2>
              <p className="text-sm text-neutral-500 mb-5">
                Each person who joined with your code gives you a reward. Pick minutes or cash — one per referral.
              </p>
              <div className="border border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0F0F0F] text-[10px] uppercase tracking-widest text-neutral-500">
                    <tr>
                      <th className="text-left px-5 py-3">Who joined</th>
                      <th className="text-left px-5 py-3 hidden sm:table-cell">Date</th>
                      <th className="px-5 py-3 text-right">Claim reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.pending_referrals.map((r) => (
                      <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="px-5 py-4">
                          <div className="font-medium text-white">{r.referee_name}</div>
                          <div className="text-xs text-neutral-500">{r.referee_email}</div>
                        </td>
                        <td className="px-5 py-4 text-neutral-500 text-xs hidden sm:table-cell">
                          {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                            <button
                              onClick={() => claim(r.id, "minutes")}
                              disabled={claiming[r.id]}
                              className="flex items-center gap-1.5 border border-[#D3FF24]/40 text-[#D3FF24] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-[#D3FF24]/10 transition disabled:opacity-50"
                            >
                              {claiming[r.id] === "minutes" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
                              +20 min
                            </button>
                            <button
                              onClick={() => claim(r.id, "payout")}
                              disabled={claiming[r.id]}
                              className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-emerald-500/20 transition disabled:opacity-50"
                            >
                              {claiming[r.id] === "payout" ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                              ₹200
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Claimed history */}
          {stats.claimed_referrals.length > 0 && (
            <div className="mb-10">
              <h2 className="font-display text-2xl font-bold tracking-tight mb-5">Reward History</h2>
              <div className="border border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0F0F0F] text-[10px] uppercase tracking-widest text-neutral-500">
                    <tr>
                      <th className="text-left px-5 py-3">Who joined</th>
                      <th className="text-left px-5 py-3 hidden sm:table-cell">Claimed on</th>
                      <th className="text-left px-5 py-3">Reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.claimed_referrals.map((r) => (
                      <tr key={r.id} className="border-t border-white/5">
                        <td className="px-5 py-3">
                          <div className="text-white">{r.referee_name}</div>
                          <div className="text-xs text-neutral-500">{r.referee_email}</div>
                        </td>
                        <td className="px-5 py-3 text-neutral-500 text-xs hidden sm:table-cell">
                          {r.reward_claimed_at ? new Date(r.reward_claimed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td className="px-5 py-3">
                          {r.reward_type === "minutes" ? (
                            <span className="flex items-center gap-1.5 text-xs text-[#D3FF24]">
                              <Clock className="w-3 h-3" /> +20 minutes
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                              <DollarSign className="w-3 h-3" /> ₹200 payout
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* How it works */}
          <div className="mb-10">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">How it works</h2>
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {HOW_IT_WORKS.map((s) => (
                <div key={s.step} className="bg-[#0A0A0A] p-8 flex flex-col">
                  <div className="font-display text-4xl font-black text-white/10 mb-4">{s.step}</div>
                  <s.icon className="w-5 h-5 text-[#D3FF24] mb-4" strokeWidth={1.75} />
                  <h3 className="font-display font-semibold text-base mb-2">{s.title}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed flex-1">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ / terms */}
          <div className="border border-white/10 bg-[#0A0A0A] p-8">
            <div className="flex items-center gap-2 mb-5">
              <Trophy className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.75} />
              <h3 className="font-display font-semibold text-sm uppercase tracking-widest">Programme details</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm text-neutral-400">
              {[
                "Referral bonus applies to new accounts only — each email can only be referred once.",
                "Your friend gets +20 bonus minutes the moment they create their account.",
                "You can claim your reward (minutes or ₹200) once per successful referral.",
                "Minutes are added to your free allowance and never expire.",
                "₹200 payouts are processed within 5–7 business days via UPI or bank transfer.",
                "There is no limit to how many people you can refer.",
                "Referral codes are unique to your account and cannot be transferred.",
                "Wingman reserves the right to cancel rewards for fraudulent or fake signups.",
              ].map((t) => (
                <div key={t} className="flex items-start gap-2.5">
                  <ChevronRight className="w-3.5 h-3.5 text-neutral-600 shrink-0 mt-0.5" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
